import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCustomerToken } from "../utils/CustomerAuth";
import { WishlistServices } from "../services/WishlistServices";
import { CartServices } from "../services/CartServices";
const API_BASE = "https://api.ifjona.com";
const toNumber = (val) => { const n = Number(String(val ?? "").replace(/[^\d.]/g, "")); return Number.isFinite(n) ? n : 0; };

// ✅ Get currency symbol from item data (same as cart)
const getCurrencySymbol = (item) => {

  // Priority 1: resolved_price currency (from backend)
  if (item?.resolved_price?.currency_symbol) {
    return item.resolved_price.currency_symbol;
  }
  
  // Priority 2: location.currency.symbol (new nested structure)
  if (item?.location?.currency?.symbol) {
    return item.location.currency.symbol;
  }
  
  // Priority 3: locationProductPrice relation
  if (item?.locationProductPrice?.location?.currency?.symbol) {
    return item.locationProductPrice.location.currency.symbol;
  }
  
  // Priority 4: productInfoLocationPrice relation
  if (item?.productInfoLocationPrice?.location?.currency?.symbol) {
    return item.productInfoLocationPrice.location.currency.symbol;
  }
  return "$";
};

// ✅ Money formatter with dynamic currency
const money = (amount, currencySymbol = "$") => {
  const result = `${currencySymbol}${Number(amount || 0).toFixed(2)}`;
  return result;
};

const toImageUrl = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/storage/")) return `${API_BASE}${s}`;
  if (s.startsWith("storage/"))  return `${API_BASE}/${s}`;
  return `${API_BASE}/storage/${s.replace(/^\/+/, "").replace(/^storage\//, "")}`;
};

const resolveImage     = (row) => toImageUrl((row?.product_info || row?.productInfo)?.image || row?.product?.image) || "/assets/images/thumbs/product-thumb-1.png";
const resolveTitle     = (row) => row?.product?.title || "Product";
const resolveDetailsUrl= (row) => row?.product?.id ? `/product-details/${row.product.id}` : "/product-details";
const resolveCategory  = (row) => row?.product?.category?.name || row?.product?.category_name || "";

// ✅ Updated: Use resolved_price from API if available, fallback to manual resolution (same as cart)
const resolveUnitPrice = (row) => {

  // Priority 1: Use backend-resolved price if available
  if (row?.resolved_price?.unit_price) {
    return toNumber(row.resolved_price.unit_price);
  }

  // Priority 2: Manual resolution (existing logic)
  const info = row?.product_info || row?.productInfo;
  const product = row?.product;

  // Flash sale price — variant-level first, then product-level
  const infoFlash    = info?.flash_sale_price ?? null;
  const productFlash = product?.flash_sale_price ?? null;

  if (infoFlash !== null && infoFlash !== "") {
    return toNumber(infoFlash);
  }
  if (productFlash !== null && productFlash !== "") {
    return toNumber(productFlash);
  }

  // Fall back to variant override, then base price
  const override = info?.current_price_override ?? null;
  const result = (override !== null && override !== "")
    ? toNumber(override)
    : toNumber(product?.base_current_price ?? 0);
  return result;
};

// ✅ Check if item has active flash sale (same as cart)
const hasFlashSale = (row) => {
  // Check resolved_price first
  if (row?.resolved_price?.is_flash_sale) {
    return true;
  }
  
  const info = row?.product_info || row?.productInfo;
  const product = row?.product;
  
  if (info?.flash_sale_price != null && info.flash_sale_price !== "") {
    return true;
  }
  
  if (product?.flash_sale_price != null && product.flash_sale_price !== "") {
    return true;
  }
  
  return false;
};

const CSS = `
  /* ── Color tokens ── */
  :root {
    --g-primary:   #299e60;
    --g-light:     #f3faf2;
    --g-border:    #c4e8d1;
    --g-hover:     #228a52;
    --g-text:      #1a5c3a;
    --flash-red:   #dc2626;
    --flash-bg:    #fef2f2;
  }

  /* ── Shared ── */
  .ws-cb        { accent-color: var(--g-primary); width:17px; height:17px; cursor:pointer; flex-shrink:0; }
  .ws-sel-row   { background: var(--g-light) !important; }
  .ws-ptitle    { font-size:13.5px; font-weight:700; color:#1d2939; text-decoration:none; line-height:1.4; }
  .ws-ptitle:hover { color: var(--g-primary); }
  .ws-cat       { font-size:10px; font-weight:700; color:#b0b8c8; text-transform:uppercase; letter-spacing:.6px; }
  .ws-xbtn      { background:none; border:none; cursor:pointer; color:#d0d5dd; padding:5px; border-radius:6px; display:flex; align-items:center; transition:color .15s,background .15s; }
  .ws-xbtn:hover { color:#d94040; background:#fff5f5; }

  /* ✅ Flash sale badge */
  .ws-flash-badge { 
    display: inline-flex; 
    align-items: center; 
    gap: 4px;
    background: var(--flash-bg); 
    color: var(--flash-red); 
    font-size: 10px; 
    font-weight: 700; 
    padding: 2px 8px; 
    border-radius: 12px; 
    margin-left: 6px;
  }

  /* ── Toolbar ── */
  .ws-toolbar   { background: var(--g-light); border:1px solid var(--g-border); border-radius:8px; padding:10px 16px; margin-bottom:16px; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; }
  .ws-sel-badge { background:#fff; color: var(--g-primary); border:1px solid var(--g-border); border-radius:20px; font-size:11px; font-weight:700; padding:2px 10px; }
  .ws-rem-sel   { background:#fff5f5; border-radius:6px; padding:5px 12px; font-size:12px; font-weight:700; color:#d94040; cursor:pointer; border: 1px solid #fcc; }
  .ws-rem-sel:hover { background:#ffe0e0; }

  /* ── Select-all label ── */
  .ws-select-label { font-size:13px; color:#344054; font-weight:700; cursor:pointer; user-select:none; display:flex; align-items:center; gap:8px; margin-bottom:0; }

  /* ── Add selected btn ── */
  .ws-add-sel-btn {
    background: var(--g-primary);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 6px 16px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: background .15s, transform .1s;
  }
  .ws-add-sel-btn:hover { background: var(--g-hover); transform: translateY(-1px); }

  /* ── Desktop table ── */
  .ws-thumb     { width:72px; height:72px; object-fit:cover; border-radius:8px; }
  .ws-thead th  { font-size:11px; letter-spacing:.8px; text-transform:uppercase; color:#98a2b3; font-weight:800; padding:12px 10px; background:#f9f9f9; border-bottom:2px solid #ececec; }
  .ws-trow td   { padding:14px 10px; border-bottom:1px solid #f5f5f5; vertical-align:middle; }
  .ws-trow:last-child td { border-bottom:none; }
  .ws-trow.ws-sel-row td { background: var(--g-light); }
  .ws-price     { font-size:14px; font-weight:700; color:#475467; white-space:nowrap; }
  .ws-price.ws-flash { color: var(--flash-red); }

  /* ── Stock badges ── */
  .ws-stock     { font-size:12px; font-weight:700; padding:3px 10px; border-radius:20px; white-space:nowrap; }
  .ws-stock.in  { background: var(--g-light); color: var(--g-text); border:1px solid var(--g-border); }
  .ws-stock.out { background:#fff5f5; color:#d94040; border:1px solid #fcc; }

  /* ── Added badge ── */
  .ws-added     { background: var(--g-light); color: var(--g-text); border:1px solid var(--g-border); border-radius:6px; padding:5px 14px; font-size:12px; font-weight:700; cursor:default; display:inline-flex; align-items:center; gap:5px; }

  /* ── Add to cart btn ── */
  .ws-cart-btn {
    background: var(--g-primary);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: background .15s, transform .1s, box-shadow .15s;
    box-shadow: 0 3px 10px rgba(41,158,96,0.2);
    white-space: nowrap;
  }
  .ws-cart-btn:hover { background: var(--g-hover); transform: translateY(-1px); box-shadow: 0 5px 14px rgba(41,158,96,0.3); }
  .ws-cart-btn.full  { width: 100%; justify-content: center; }

  /* ── Mobile cards ── */
  .ws-mcard { border:1px solid #ececec; border-radius:12px; margin-bottom:12px; overflow:hidden; transition:border-color .15s; }
  .ws-mcard.ws-sel-row { background: var(--g-light); border-color: var(--g-border); }

  .ws-m-top { display:flex; align-items:flex-start; gap:12px; padding:14px 14px 10px; }
  .ws-mimg  { width:80px; height:80px; object-fit:cover; border-radius:10px; border:1px solid #ececec; flex-shrink:0; }
  .ws-m-info { flex:1; min-width:0; }
  .ws-m-title-row { display:flex; justify-content:space-between; align-items:flex-start; gap:8px; margin-bottom:4px; }

  .ws-m-bottom { display:flex; align-items:center; justify-content:space-between; padding:10px 14px 12px; border-top:1px solid #f5f5f5; background: var(--g-light); gap:10px; flex-wrap:wrap; }
  .ws-m-pricebox { display:flex; flex-direction:column; gap:2px; }
  .ws-m-plabel   { font-size:10px; font-weight:700; color:#b0b8c8; text-transform:uppercase; letter-spacing:.5px; }
  .ws-m-pval     { font-size:13px; font-weight:700; color:#475467; }
  .ws-m-pval.ws-flash { color: var(--flash-red); }

  /* ── Summary bar ── */
  .ws-summary {
    background: var(--g-light);
    border: 1px solid var(--g-border);
    border-radius: 8px;
    padding: 13px 18px;
    margin-top: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 13px;
  }

  .ws-summary-total { color: var(--g-primary); font-weight: 800; font-size: 15px; }

  /* ── Spinner ── */
  .ws-spinner { width:36px; height:36px; border:3px solid var(--g-light); border-top-color: var(--g-primary); border-radius:50%; animation: wsSpin .7s linear infinite; margin: 0 auto 16px; }
  @keyframes wsSpin { to { transform: rotate(360deg); } }
`;

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
  </svg>
);

const CartIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
  </svg>
);

const WishListSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading]         = useState(false);
  const [rows, setRows]               = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [addedIds, setAddedIds]       = useState(new Set());

  useEffect(() => {
    if (!getCustomerToken()) navigate("/login", { state: { from: "/wishlist" } });
  }, [navigate]);

  const loadWishlist = useCallback(async () => {
    if (!getCustomerToken()) { setRows([]); return; }
    try {
      setLoading(true);
      const items = await WishlistServices.getWishlist();
      const arr = Array.isArray(items) ? items : [];
      setRows(arr);
      setSelectedIds(prev => prev.size === 0 && arr.length > 0 ? new Set(arr.map(r => String(r.id))) : prev);
    } catch (err) {
      console.error("❌ Failed to load wishlist:", err);
      if ([401, 403].includes(err?.response?.status)) navigate("/login", { state: { from: "/wishlist" } });
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    loadWishlist();
    const evts = ["wishlist_updated", "customer_logged_in", "customer_logged_out", "customer_auth_changed"];
    evts.forEach(e => window.addEventListener(e, loadWishlist));
    return () => evts.forEach(e => window.removeEventListener(e, loadWishlist));
  }, [loadWishlist]);

  const removeItem = async (id) => {
    try {
      await WishlistServices.remove(id);
      setSelectedIds(prev => { const n = new Set(prev); n.delete(String(id)); return n; });
      setAddedIds(prev => { const n = new Set(prev); n.delete(String(id)); return n; });
      await loadWishlist();
      window.dispatchEvent(new Event("wishlist_updated"));
    } catch (err) { console.error("Remove wishlist failed:", err); }
  };

  const removeSelected = async () => {
    for (const id of selectedIds) { try { await WishlistServices.remove(id); } catch {} }
    setSelectedIds(new Set());
    await loadWishlist();
    window.dispatchEvent(new Event("wishlist_updated"));
  };

  // ✅ FIXED: Now includes product_info_location_price_id
  const addToCart = async (row) => {
    if (!getCustomerToken()) { navigate("/login", { state: { from: "/wishlist" } }); return; }
    
    try {
      await CartServices.add({
        product_id:                     row?.product?.id,
        product_info_id:                row?.product_info?.id ?? row?.productInfo?.id ?? null,
        quantity:                       1,
        location_product_price_id:      row?.location_product_price_id ?? null,
        product_info_location_price_id: row?.product_info_location_price_id ?? null, // ✅ CRITICAL FIX
      });
      setAddedIds(prev => new Set([...prev, String(row.id)]));
      window.dispatchEvent(new Event("cart_updated"));
    } catch (err) {
      console.error("Add to cart failed:", err);
      if ([401, 403].includes(err?.response?.status)) navigate("/login", { state: { from: "/wishlist" } });
    }
  };

  const addSelectedToCart = async () => {
    const selected = rows.filter(r => selectedIds.has(String(r.id)));
    for (const row of selected) { try { await addToCart(row); } catch {} }
  };

  const toggleSelect = (id) =>
    setSelectedIds(prev => { const n = new Set(prev); n.has(String(id)) ? n.delete(String(id)) : n.add(String(id)); return n; });

  const allSelected  = rows.length > 0 && rows.every(r => selectedIds.has(String(r.id)));
  const someSelected = selectedIds.size > 0;
  const toggleAll    = () => setSelectedIds(allSelected ? new Set() : new Set(rows.map(r => String(r.id))));

  // ✅ Updated: Calculate total value with proper currency handling (same as cart)
  const { totalValue, currencySymbol } = useMemo(() => {
    
    // Get currency from first item (all should have same currency in wishlist)
    const firstItem = rows[0];
    const symbol = firstItem ? getCurrencySymbol(firstItem) : "$";
    
    const total = rows.reduce((s, r) => s + resolveUnitPrice(r), 0);
    
    return { totalValue: total, currencySymbol: symbol };
  }, [rows]);

  const StockBadge = ({ row }) => {
    const inStock = row?.product?.stock_quantity > 0 || row?.product?.in_stock !== false;
    return <span className={`ws-stock ${inStock ? "in" : "out"}`}>{inStock ? "✓ In Stock" : "Out of Stock"}</span>;
  };

  const AddBtn = ({ row, full = false }) => {
    const isAdded = addedIds.has(String(row.id));
    return isAdded
      ? <span className="ws-added">✓ Added to Cart</span>
      : <button type="button" className={`ws-cart-btn${full ? " full" : ""}`} onClick={() => addToCart(row)}>
          <CartIcon /> Add to Cart
        </button>;
  };

  return (
    <section className="cart py-80">
      <style>{CSS}</style>
      <div className="container container-lg">
        <div className="row gy-4">
          <div className="col-12">
            <div className="cart-table border border-gray-100 rounded-8 px-24 px-sm-32 py-32 py-sm-40">

              {/* Loading */}
              {loading ? (
                <div className="py-48 text-center">
                  <div className="ws-spinner" />
                  <div className="text-gray-500 fw-semibold">Loading wishlist…</div>
                </div>

              /* Empty */
              ) : rows.length === 0 ? (
                <div className="py-48 text-center text-gray-500">
                  <div style={{ fontSize: 48 }} className="mb-16">🤍</div>
                  <p className="mb-16 fw-semibold">Your wishlist is empty.</p>
                  <Link to="/shop" className="btn btn-main py-12 px-32 rounded-8">Go to Shop</Link>
                </div>

              ) : (
                <>
                  {/* ── Toolbar ── */}
                  <div className="ws-toolbar">
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <label className="ws-select-label">
                        <input type="checkbox" className="ws-cb" checked={allSelected} onChange={toggleAll} />
                        {allSelected ? "Deselect All" : "Select All"}
                      </label>
                      {someSelected && (
                        <span className="ws-sel-badge">{selectedIds.size} of {rows.length} selected</span>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {someSelected && (
                        <button className="ws-add-sel-btn" onClick={addSelectedToCart}>
                          <CartIcon /> Add Selected to Cart
                        </button>
                      )}
                      {selectedIds.size > 1 && (
                        <button className="ws-rem-sel" onClick={removeSelected}>
                          Remove Selected ({selectedIds.size})
                        </button>
                      )}
                    </div>
                  </div>

                  {/* ── Desktop Table ── */}
                  <div className="d-none d-md-block overflow-x-auto scroll-sm">
                    <table className="table style-three w-100">
                      <tbody>
                        {rows.map((row, idx) => {
                          const id = String(row.id), sel = selectedIds.has(id);
                          const unitPrice = resolveUnitPrice(row);
                          const isFlash = hasFlashSale(row);
                          const itemCurrency = getCurrencySymbol(row);
                          
                          
                          return (
                            <tr key={`${id}-${idx}`} className={`ws-trow${sel ? " ws-sel-row" : ""}`}>
                              <td><input type="checkbox" className="ws-cb" checked={sel} onChange={() => toggleSelect(id)} /></td>
                              <td>
                                <div className="d-flex align-items-center gap-16">
                                  <Link to={resolveDetailsUrl(row)} className="border border-gray-100 rounded-8 flex-center" style={{ width: 72, height: 72, flexShrink: 0 }}>
                                    <img src={resolveImage(row)} alt={resolveTitle(row)} className="ws-thumb"
                                      onError={e => { e.currentTarget.src = "/assets/images/thumbs/product-thumb-1.png"; }} />
                                  </Link>
                                  <div>
                                    <div className="d-flex align-items-center">
                                      <Link to={resolveDetailsUrl(row)} className="ws-ptitle d-block mb-4">
                                        {resolveTitle(row)}
                                      </Link>
                                      {isFlash && (
                                        <span className="ws-flash-badge">🔥 Flash Sale</span>
                                      )}
                                    </div>
                                    {resolveCategory(row) && <span className="ws-cat">{resolveCategory(row)}</span>}
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className={`ws-price${isFlash ? " ws-flash" : ""}`}>
                                  {money(unitPrice, itemCurrency)}
                                </span>
                              </td>
                              <td><StockBadge row={row} /></td>
                              <td><AddBtn row={row} /></td>
                              <td>
                                <button className="ws-xbtn" onClick={() => removeItem(row.id)} title="Remove"><TrashIcon /></button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* ── Mobile Cards ── */}
                  <div className="d-md-none">
                    {rows.map((row, idx) => {
                      const id = String(row.id), sel = selectedIds.has(id);
                      const unitPrice = resolveUnitPrice(row);
                      const isFlash = hasFlashSale(row);
                      const itemCurrency = getCurrencySymbol(row);
                      
                      return (
                        <div key={`${id}-${idx}`} className={`ws-mcard${sel ? " ws-sel-row" : ""}`}>
                          <div className="ws-m-top">
                            <input type="checkbox" className="ws-cb" style={{ marginTop: 4 }}
                              checked={sel} onChange={() => toggleSelect(id)} />
                            <Link to={resolveDetailsUrl(row)} style={{ flexShrink: 0 }}>
                              <img src={resolveImage(row)} alt={resolveTitle(row)} className="ws-mimg"
                                onError={e => { e.currentTarget.src = "/assets/images/thumbs/product-thumb-1.png"; }} />
                            </Link>
                            <div className="ws-m-info">
                              <div className="ws-m-title-row">
                                <div>
                                  <Link to={resolveDetailsUrl(row)} className="ws-ptitle d-block">
                                    {resolveTitle(row)}
                                  </Link>
                                  {isFlash && (
                                    <span className="ws-flash-badge" style={{ marginLeft: 0, marginTop: 4 }}>
                                      🔥 Flash Sale
                                    </span>
                                  )}
                                </div>
                                <button className="ws-xbtn" onClick={() => removeItem(row.id)} title="Remove"><TrashIcon /></button>
                              </div>
                              {resolveCategory(row) && <span className="ws-cat">{resolveCategory(row)}</span>}
                            </div>
                          </div>
                          <div className="ws-m-bottom">
                            <div className="ws-m-pricebox">
                              <span className="ws-m-plabel">Unit Price</span>
                              <span className={`ws-m-pval${isFlash ? " ws-flash" : ""}`}>
                                {money(unitPrice, itemCurrency)}
                              </span>
                            </div>
                            <StockBadge row={row} />
                            <AddBtn row={row} />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* ── Summary bar ── */}
                  <div className="ws-summary">
                    <span className="text-gray-500 fw-semibold">
                      {rows.length} {rows.length === 1 ? "item" : "items"} saved
                    </span>
                    <span className="fw-bold" style={{ color: "#1d2939" }}>
                      Total value: <span className="ws-summary-total">{money(totalValue, currencySymbol)}</span>
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WishListSection;